/**
 * The Contact block. Email is always there; everything else is optional in the
 * schema, so "Show more" only appears when there is genuinely more to show.
 */
import { useState } from 'react';
import { AtSign, Building2, ChevronDown, ChevronUp, Mail, MapPin, Phone } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@cyphex/ui';
import type { User } from '@cyphex/types/identity';

export interface ProfileContactProps {
  user: User;
  className?: string;
}

interface ContactLine {
  readonly id: string;
  readonly icon: LucideIcon;
  readonly label: string;
  readonly value: string;
  /** Rendered as a link when set — mailto: / tel:. */
  readonly href?: string;
}

function extraLinesOf(user: User): ContactLine[] {
  const lines: ContactLine[] = [];
  if (user.phone !== null && user.phone !== '') {
    lines.push({
      id: 'phone', icon: Phone, label: 'Phone', value: user.phone,
      href: `tel:${user.phone.replace(/\s+/g, '')}`,
    });
  }
  if (user.department !== null && user.department !== '') {
    lines.push({ id: 'department', icon: Building2, label: 'Department', value: user.department });
  }
  if (user.officeLocation !== null && user.officeLocation !== '') {
    lines.push({ id: 'office', icon: MapPin, label: 'Office', value: user.officeLocation });
  }
  lines.push({ id: 'username', icon: AtSign, label: 'Username', value: user.username });
  return lines;
}

function ContactRow({ line }: { line: ContactLine }) {
  const Icon = line.icon;
  return (
    <li className="flex items-start gap-2.5">
      <Icon size={16} className="mt-0.5 shrink-0 text-muted-foreground" aria-hidden />
      <span className="min-w-0">
        <span className="sr-only">{line.label}: </span>
        {line.href === undefined ? (
          <span className="block break-words text-md text-foreground">{line.value}</span>
        ) : (
          <a
            href={line.href}
            className="block break-words text-md text-primary hover:underline focus-visible:underline"
          >
            {line.value}
          </a>
        )}
      </span>
    </li>
  );
}

export function ProfileContact({ user, className }: ProfileContactProps) {
  const [expanded, setExpanded] = useState(false);
  const extras = extraLinesOf(user);

  return (
    <section className={cn('space-y-2', className)} aria-labelledby={`contact-${user.id}`}>
      <h4
        id={`contact-${user.id}`}
        className="text-sm font-semibold uppercase tracking-wide text-muted-foreground"
      >
        Contact
      </h4>
      <ul className="space-y-2">
        <ContactRow
          line={{ id: 'email', icon: Mail, label: 'Email', value: user.email, href: `mailto:${user.email}` }}
        />
        {expanded ? extras.map((line) => <ContactRow key={line.id} line={line} />) : null}
      </ul>
      {extras.length > 0 ? (
        <button
          type="button"
          onClick={() => setExpanded((open) => !open)}
          aria-expanded={expanded}
          className="inline-flex items-center gap-1 rounded-md text-md font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {expanded ? 'Show less' : 'Show more'}
          {expanded ? <ChevronUp size={14} aria-hidden /> : <ChevronDown size={14} aria-hidden />}
        </button>
      ) : null}
    </section>
  );
}
